/**
 * Computes the mode of a list of numbers.
 * @param list Array<number> The list of numbers.
 * @return number | undefined Returns the most commonly seen number or undefined if the list is
 *         empty.
 */
export const mode = (list: Array<number>): number | undefined => {
  const mapping: { [key: string]: number } = {};
  let highestCount = 0;
  let toReturn;
  for (const num of list) {
    const asString = num.toString();
    mapping[asString] = asString in mapping ? mapping[asString] + 1 : 1;
    if (mapping[asString] > highestCount) {
      highestCount = mapping[asString];
      toReturn = num;
    }
  }
  return toReturn;
};

/**
 * Computes the mean (average) of a list of numbers.
 * @param list Array<number> The list of numbers.
 * @return number | undefined Returns the average of the list or undefined if the list is empty.
 */
export const mean = (list: Array<number>): number | undefined => {
  if (list.length < 1) {
    return undefined;
  }
  return list.reduce((sum, next) => sum + next, 0) / list.length;
};

/**
 * Computes the standard deviation of a list of numbers.
 * @param list Array<number> The list of numbers.
 * @return number | undefined Returns the std dev of the list or undefined if the list has fewer
 *         than 2 elements.
 */
export const stdDev = (list: Array<number>): number | undefined => {
  const avg = mean(list);
  if (avg === undefined || list.length < 2) {
    return undefined;
  }
  return Math.sqrt(
    list
      .map(n => {
        const diff = n - avg;
        return diff * diff;
      })
      .reduce((sum, next) => sum + next, 0) / list.length,
  );
};
